// src/admin/pages/car/QuickAddDrawers.tsx
//
// Lightweight "quick add" drawers used from inside the Add/Edit Product
// drawer so an admin who can't find a category, subcategory, brand or
// variant type doesn't have to abandon the product form to go create one
// elsewhere. Each drawer creates exactly one record via the same services
// the full management pages use, then hands the new record back to the
// caller so it can be appended to the in-memory list and auto-selected.
import React, { useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Stack,
  Paper,
  Avatar,
  Tooltip,
  useTheme,
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';
import { Category, CategoryFormData } from '../../../enums/product';
import { CategoryService } from '../category/categoryService';
import { createBrand, Brand } from '../brand/brandService';
import { createCarModel } from '../carmodel/carModelService';
import { CarModel, Trim, CarModelFormData } from '../carmodel/carModel';

type ToastFn = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => void;

// ─────────────────────────────────────────────────────────────────────────────
// Shared drawer chrome
// ─────────────────────────────────────────────────────────────────────────────
const QuickAddShell: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  saving: boolean;
  saveLabel: string;
  saveDisabled: boolean;
  onSave: () => void;
  children: React.ReactNode;
}> = ({ open, title, onClose, saving, saveLabel, saveDisabled, onSave, children }) => (
  <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 440 }, display: 'flex', flexDirection: 'column' } }}>
    <AppBar position="static" color="transparent" elevation={0} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
        <IconButton onClick={onClose} disabled={saving}><CloseIcon /></IconButton>
      </Toolbar>
    </AppBar>
    <Box sx={{ p: 3, flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
      {children}
    </Box>
    <Box sx={{ p: 2.5, display: 'flex', gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Button onClick={onClose} variant="outlined" disabled={saving} fullWidth>Cancel</Button>
      <Button
        onClick={onSave}
        variant="contained"
        disabled={saving || saveDisabled}
        fullWidth
        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
      >
        {saving ? 'Creating...' : saveLabel}
      </Button>
    </Box>
  </Drawer>
);

// ─────────────────────────────────────────────────────────────────────────────
// QUICK ADD CATEGORY / SUBCATEGORY
// ─────────────────────────────────────────────────────────────────────────────
interface QuickAddCategoryDrawerProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  /** When set, the new category is created as a subcategory under this parent. */
  parentId?: string | null;
  parentName?: string;
  onCreated: (category: Category) => void;
  onShowToast: ToastFn;
}

export const QuickAddCategoryDrawer: React.FC<QuickAddCategoryDrawerProps> = ({
  open, onClose, userId, parentId, parentName, onCreated, onShowToast,
}) => {
  const isSubcategory = !!parentId;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = () => { setName(''); setDescription(''); };
  const handleClose = () => { if (saving) return; resetForm(); onClose(); };

  const handleSave = async () => {
    if (!name.trim()) { onShowToast('Name is required', 'error'); return; }
    if (!userId) { onShowToast('You must be logged in', 'error'); return; }

    setSaving(true);
    try {
      const formData: CategoryFormData = {
        name: name.trim(),
        description: description.trim(),
        parentId: parentId || '',
        isActive: true,
        sortOrder: '0',
      };
      const id = await CategoryService.createCategory(formData, userId, null);
      onCreated({
        id,
        name: name.trim(),
        description: description.trim() || undefined,
        parentId: parentId || undefined,
        isActive: true,
        sortOrder: 0,
      });
      onShowToast(`${isSubcategory ? 'Subcategory' : 'Category'} created successfully`, 'success');
      resetForm();
      onClose();
    } catch (err: any) {
      onShowToast(err?.message || `Failed to create ${isSubcategory ? 'subcategory' : 'category'}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickAddShell
      open={open}
      title={isSubcategory ? 'Add New Subcategory' : 'Add New Category'}
      onClose={handleClose}
      saving={saving}
      saveLabel={isSubcategory ? 'Create Subcategory' : 'Create Category'}
      saveDisabled={!name.trim()}
      onSave={handleSave}
    >
      {isSubcategory && parentName && (
        <Alert severity="info" sx={{ borderRadius: 1.5 }}>Will be added under <strong>{parentName}</strong></Alert>
      )}
      <TextField
        label="Name *" fullWidth size="small" autoFocus
        value={name} onChange={(e) => setName(e.target.value)}
        placeholder={isSubcategory ? 'e.g., SUVs' : 'e.g., Vehicles'}
      />
      <TextField
        label="Description" fullWidth size="small" multiline rows={3}
        value={description} onChange={(e) => setDescription(e.target.value)}
      />
    </QuickAddShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QUICK ADD BRAND
// ─────────────────────────────────────────────────────────────────────────────
interface QuickAddBrandDrawerProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  onCreated: (brand: Brand) => void;
  onShowToast: ToastFn;
}

export const QuickAddBrandDrawer: React.FC<QuickAddBrandDrawerProps> = ({
  open, onClose, userId, onCreated, onShowToast,
}) => {
  const theme = useTheme();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [saving, setSaving] = useState(false);

  const resetForm = () => {
    setName(''); setDescription(''); setWebsiteUrl('');
    setImageFile(null); setImagePreview('');
  };
  const handleClose = () => { if (saving) return; resetForm(); onClose(); };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!name.trim()) { onShowToast('Brand name is required', 'error'); return; }
    if (!imageFile) { onShowToast('Please select a brand image', 'error'); return; }
    if (!userId) { onShowToast('You must be logged in', 'error'); return; }

    setSaving(true);
    try {
      const result = await createBrand(
        {
          name: name.trim(),
          description: description.trim(),
          websiteUrl: websiteUrl.trim() || undefined,
          isActive: true,
          featured: false,
          createdBy: '',
        },
        imageFile,
        userId
      );
      if (result.success && result.brandId) {
        onCreated({
          id: result.brandId,
          name: name.trim(),
          description: description.trim(),
          imageUrl: imagePreview,
          slug: '',
          isActive: true,
          featured: false,
          websiteUrl: websiteUrl.trim() || undefined,
          createdBy: userId,
          createdAt: new Date(),
          updatedAt: new Date(),
        });
        onShowToast('Brand created successfully', 'success');
        resetForm();
        onClose();
      } else {
        onShowToast(result.message || 'Failed to create brand', 'error');
      }
    } catch (err: any) {
      onShowToast(err?.message || 'Failed to create brand', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickAddShell
      open={open}
      title="Add New Brand"
      onClose={handleClose}
      saving={saving}
      saveLabel="Create Brand"
      saveDisabled={!name.trim() || !imageFile}
      onSave={handleSave}
    >
      <TextField label="Name *" fullWidth size="small" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Toyota" />
      <TextField label="Description" fullWidth size="small" multiline rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
      <TextField label="Website URL" fullWidth size="small" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} placeholder="https://..." />

      <Box>
        <Typography variant="caption" sx={{ display: 'block', mb: 1, fontWeight: 500, color: 'text.secondary' }}>Logo Image *</Typography>
        <Stack direction="row" alignItems="center" spacing={2}>
          {imagePreview ? (
            <Avatar src={imagePreview} variant="rounded" sx={{ width: 56, height: 56 }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: theme.palette.action.hover }}>
              <StorefrontIcon color="disabled" />
            </Avatar>
          )}
          <Button component="label" variant="outlined" size="small" startIcon={<CloudUploadIcon />}>
            {imageFile ? 'Change Image' : 'Upload Image'}
            <input type="file" hidden accept="image/*" onChange={handleImageChange} />
          </Button>
        </Stack>
      </Box>
    </QuickAddShell>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// QUICK ADD MODEL (with trims)
// ─────────────────────────────────────────────────────────────────────────────
interface QuickAddModelDrawerProps {
  open: boolean;
  onClose: () => void;
  userId: string;
  /** Pre-fills the brand this model belongs to (usually the brand already chosen in the Car form). */
  brandId?: string;
  brandName?: string;
  onCreated: (carModel: CarModel) => void;
  onShowToast: ToastFn;
}

const EMPTY_TRIM: Trim = { name: '', description: '', isActive: true };

export const QuickAddModelDrawer: React.FC<QuickAddModelDrawerProps> = ({
  open, onClose, userId, brandId, brandName, onCreated, onShowToast,
}) => {
  const [name, setName] = useState('');
  const [trims, setTrims] = useState<Trim[]>([{ ...EMPTY_TRIM }]);
  const [saving, setSaving] = useState(false);

  const resetForm = () => { setName(''); setTrims([{ ...EMPTY_TRIM }]); };
  const handleClose = () => { if (saving) return; resetForm(); onClose(); };

  const updateTrim = (index: number, field: 'name' | 'description', value: string) => {
    setTrims(prev => prev.map((t, i) => (i === index ? { ...t, [field]: value } : t)));
  };
  const addTrim = () => setTrims(prev => [...prev, { ...EMPTY_TRIM }]);
  const removeTrim = (index: number) => setTrims(prev => prev.filter((_, i) => i !== index));

  const cleanTrims = trims.filter(t => t.name.trim());

  const handleSave = async () => {
    if (!name.trim()) { onShowToast('Model name is required', 'error'); return; }
    if (!brandId) { onShowToast('Select a brand first', 'error'); return; }
    if (!userId) { onShowToast('You must be logged in', 'error'); return; }

    setSaving(true);
    try {
      const formData: CarModelFormData = {
        name: name.trim(),
        brandId,
        isActive: true,
        trims: cleanTrims.map(t => ({ name: t.name.trim(), description: t.description?.trim() || undefined, isActive: true })),
      };
      const result = await createCarModel(formData, userId);
      if (result.success && result.carModelId) {
        onCreated({
          id: result.carModelId,
          name: name.trim(),
          brandId,
          brandName,
          isActive: true,
          trims: cleanTrims.map((t, i) => ({ id: `temp-${i}`, name: t.name.trim(), description: t.description?.trim(), isActive: true })),
        });
        onShowToast('Model created successfully', 'success');
        resetForm();
        onClose();
      } else {
        onShowToast(result.message || 'Failed to create model', 'error');
      }
    } catch (err: any) {
      onShowToast(err?.message || 'Failed to create model', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <QuickAddShell
      open={open}
      title="Add New Model"
      onClose={handleClose}
      saving={saving}
      saveLabel="Create Model"
      saveDisabled={!name.trim() || !brandId}
      onSave={handleSave}
    >
      {brandName ? (
        <Alert severity="info" sx={{ borderRadius: 1.5 }}>Will be added under <strong>{brandName}</strong></Alert>
      ) : (
        <Alert severity="warning" sx={{ borderRadius: 1.5 }}>Select a brand on the car form first.</Alert>
      )}
      <TextField label="Model Name *" fullWidth size="small" autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g., Corolla" />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Trims (Optional)</Typography>
        <Button onClick={addTrim} startIcon={<AddIcon />} size="small">Add Trim</Button>
      </Stack>

      <Stack spacing={1.5}>
        {trims.map((trim, index) => (
          <Paper key={index} variant="outlined" sx={{ p: 1.5, borderRadius: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Trim Name" size="small" fullWidth value={trim.name}
                onChange={(e) => updateTrim(index, 'name', e.target.value)}
                placeholder="e.g., GLi" autoFocus={index === trims.length - 1 && index > 0}
              />
              <TextField
                label="Description (optional)" size="small" fullWidth value={trim.description || ''}
                onChange={(e) => updateTrim(index, 'description', e.target.value)}
                placeholder="e.g., 1.8L, CVT"
              />
              <Tooltip title="Remove Trim">
                <span>
                  <IconButton onClick={() => removeTrim(index)} color="error" size="small" disabled={trims.length === 1}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </QuickAddShell>
  );
};
